// SHADOW MARKET™ — Kids Lesson detail: animated lesson → mini-game → continue.
import React, { useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Star } from "lucide-react";
import { ASSETS } from "../assets";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useLanguage } from "../i18n/LanguageContext";
import { LeccionAnimada } from "../components/kids/LeccionAnimada";
import { KidsGame } from "../components/kids/KidsGames";
import { LECCIONES_KIDS, getLeccionKids } from "../data/kidsLecciones";
import { getKidsUi } from "../i18n/kids-academy";
import { setLeccionCompleted, scoreToStars } from "../utils/kidsProgress";

const KidsLeccionPage = () => {
  const { id } = useParams();
  const { lang } = useLanguage();
  const ui = getKidsUi(lang);
  const navigate = useNavigate();

  const leccion = getLeccionKids(id);
  const [phase, setPhase] = useState("lesson"); // 'lesson' | 'gate' | 'game' | 'done'
  const [score, setScore] = useState(0);
  const [stars, setStars] = useState(1);

  if (!leccion) return <Navigate to="/kids" replace />;

  const idx = LECCIONES_KIDS.findIndex((l) => l.id === id);
  const next = LECCIONES_KIDS[idx + 1] || null;
  const titulo = leccion.titulo[lang] || leccion.titulo.es;

  const onLessonDone = () => setPhase("gate");
  const onGameDone = (gameScore) => {
    const s = scoreToStars(gameScore);
    setScore(gameScore);
    setStars(s);
    setLeccionCompleted(id, s);
    setPhase("done");
  };
  const skipGame = () => {
    setLeccionCompleted(id, 1);
    setStars(1);
    setPhase("done");
  };

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-black text-white">
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,rgba(245,166,35,0.16),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_100%,rgba(0,200,100,0.16),transparent_50%)]" />
      </div>

      <header className="relative z-20 border-b border-white/5 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3 md:px-10 md:py-4">
          <Link
            to="/kids"
            className="inline-flex items-center gap-2 text-white/70 transition-colors hover:text-white"
            data-testid="kids-back-map"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-xs font-bold uppercase tracking-[0.2em]">{ui.back_to_map}</span>
          </Link>
          <img src={ASSETS.logos.rebel} alt="REBEL" className="h-8 w-auto md:h-10" />
          <LanguageSwitcher />
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-3xl px-4 py-8 md:px-10">
        <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">
          {ui.level} {leccion.nivel} · {ui.mission} {idx + 1}/{LECCIONES_KIDS.length}
        </p>
        <h1
          className="mb-6 font-display text-3xl font-black tracking-tight md:text-4xl"
          data-testid="kids-leccion-title"
        >
          {titulo}
        </h1>

        {phase === "lesson" && (
          <LeccionAnimada
            personaje={leccion.personaje}
            slides={leccion.slides}
            lang={lang}
            onComplete={onLessonDone}
          />
        )}

        {phase === "gate" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="rounded-2xl border border-emerald-400/30 bg-gradient-to-br from-emerald-400/10 to-orange-400/10 p-8 text-center backdrop-blur-xl"
            data-testid="kids-lesson-gate"
          >
            <div className="mb-3 text-5xl">🎉</div>
            <h2 className="mb-2 text-2xl font-black text-white">{ui.lesson_done_title}</h2>
            <p className="mb-6 text-sm text-white/65">{ui.lesson_done_sub}</p>
            <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
              <button
                onClick={() => setPhase("game")}
                data-testid="kids-play-game-btn"
                className="rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-3 text-xs font-black uppercase tracking-widest text-black shadow-[0_0_25px_rgba(245,166,35,.4)] transition-transform hover:-translate-y-0.5"
              >
                {ui.play_game} 🎮
              </button>
              <button
                onClick={skipGame}
                className="rounded-xl border border-white/10 bg-white/[0.04] px-5 py-3 text-xs font-bold uppercase tracking-widest text-white/65 transition-colors hover:border-white/25"
              >
                {ui.skip_game}
              </button>
            </div>
          </motion.div>
        )}

        {phase === "game" && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl md:p-6">
            <KidsGame id={leccion.minijuego} lang={lang} onComplete={onGameDone} />
          </div>
        )}

        {phase === "done" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-400/15 to-orange-400/10 p-8 text-center backdrop-blur-xl"
            data-testid="kids-lesson-done"
          >
            <div className="mb-3 text-5xl">🏆</div>
            <h2 className="mb-2 text-2xl font-black text-white">{ui.badge_earned}</h2>
            {score > 0 && (
              <div className="mb-3 text-sm text-white/70">
                {ui.game_score.replace("{n}", score)}
              </div>
            )}
            <div className="mb-6 flex items-center justify-center gap-1.5">
              {[1, 2, 3].map((s) => (
                <Star
                  key={s}
                  className="h-7 w-7 text-amber-300"
                  fill={s <= stars ? "currentColor" : "transparent"}
                />
              ))}
            </div>
            <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
              {next ? (
                <button
                  onClick={() => navigate(`/kids/leccion/${next.id}`)}
                  data-testid="kids-next-leccion"
                  className="rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-3 text-xs font-black uppercase tracking-widest text-black shadow-[0_0_25px_rgba(245,166,35,.4)] transition-transform hover:-translate-y-0.5"
                >
                  {ui.game_continue}
                </button>
              ) : (
                <Link
                  to="/kids/logros"
                  className="rounded-xl bg-gradient-to-r from-amber-500 to-pink-500 px-6 py-3 text-xs font-black uppercase tracking-widest text-black"
                >
                  🏆 {ui.achievements}
                </Link>
              )}
              <Link
                to="/kids"
                className="rounded-xl border border-white/10 bg-white/[0.04] px-5 py-3 text-xs font-bold uppercase tracking-widest text-white/65 transition-colors hover:border-white/25"
              >
                {ui.back_to_map}
              </Link>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default KidsLeccionPage;
